import { Typography } from '@sds-system/components/Typography';
import type { CSSProperties, PolymorphicRef, TextColorCode } from '@sds-system/types';
import type { ElementType, ReactNode } from 'react';
import { createContext, forwardRef, useContext, useEffect, useState } from 'react';
import { Column } from '../../Column';
import {
  InnerTextField,
  type TextFieldComponent,
  type TextFieldProps,
  type TextFieldStatus,
  type TextFieldVariant,
} from '../InnerTextField';

type TextFieldComposition = TextFieldComponent & { HelperText: typeof HelperText };

const TextFieldContext = createContext<{ status: TextFieldStatus } | null>(null);

const TextField = forwardRef(
  <C extends ElementType = 'input', U extends TextFieldVariant = 'normal'>(
    props: TextFieldProps<C, U>,
    ref: PolymorphicRef<C>,
  ) => {
    const { children, helperText, ...restProps } = props;
    const [status, setStatus] = useState<TextFieldStatus>('none');
    const hasHelperText =
      (helperText?.some((text) => text.alwaysVisible || text.type === status) &&
        (helperText?.length || 0) > 0) ||
      children !== undefined;

    useEffect(() => {
      if (!props.status) return;
      setStatus(props.status);
    }, [props.status]);

    return (
      <TextFieldContext.Provider value={{ status }}>
        <Column gap={8} width='100%'>
          <InnerTextField ref={ref} {...(restProps as TextFieldProps<C, U>)} />

          {hasHelperText && (
            <Column gap={4}>
              {helperText?.map((helperTextProps, idx) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: unique값 없음
                <HelperText key={idx} {...helperTextProps} />
              ))}
              {children}
            </Column>
          )}
        </Column>
      </TextFieldContext.Provider>
    );
  },
) as unknown as TextFieldComposition;

export const useTextFieldContext = () => {
  const context = useContext(TextFieldContext);
  if (!context)
    throw new Error('useTextFieldContext must be used within TextFieldContext.Provider');
  return context;
};

export interface HelperTextProps {
  alwaysVisible?: boolean;
  children: ReactNode;
  textAlign?: CSSProperties['textAlign'];
  type?: TextFieldStatus;
}

const HelperText = (props: HelperTextProps) => {
  const { alwaysVisible, children, textAlign = 'start', type = 'none' } = props;
  const { status } = useTextFieldContext();

  if (!alwaysVisible && type !== status) return null;

  return (
    <Typography
      color={type === 'none' ? 'text.assistance' : (`text.${[type]}` as TextColorCode)}
      css={{ textAlign }}
      fontType='caption.regular'
    >
      {children}
    </Typography>
  );
};

TextField.HelperText = HelperText;

export default TextField;
